/**
 * @fileoverview Composable for speech-to-text functionality
 * @module composables/useSpeechToText
 *
 * Manages speech recognition state and controls for
 * voice input in the chat window.
 *
 * @vue3migration - Phase 3E: Extracted from ChatWindow/Index.vue
 */
import { ref, type Ref } from 'vue'

/**
 * Voice recognition interface from $listen global
 */
interface VoiceRecognition {
  start: () => void
  stop: () => void
}

/**
 * Listen function type from global properties
 */
type ListenFunction = (
  callback: (text: string, complete?: boolean) => void
) => VoiceRecognition

/**
 * Speak function type from global properties
 */
type SpeakFunction = (text: string) => void

/**
 * Options for useSpeechToText composable
 */
interface UseSpeechToTextOptions {
  /** $listen global function for speech recognition */
  $listen?: ListenFunction
  /** $speak global function for text-to-speech */
  $speak?: SpeakFunction
  /** Ref to chatbox text value */
  chatbox: Ref<string>
  /** Ref to showSuggestion state */
  showSuggestion: Ref<boolean>
  /** Send function to call after voice input */
  send: () => void
}

/**
 * Composable for speech-to-text functionality
 *
 * @param options - Dependencies and callbacks
 * @returns Speech recognition state and control methods
 *
 * @example
 * ```ts
 * const {
 *   voice,
 *   listening,
 *   showPushToTalk,
 *   toggleVoice,
 *   startListen,
 *   stopListen,
 *   speak,
 * } = useSpeechToText({
 *   $listen,
 *   $speak,
 *   chatbox,
 *   showSuggestion,
 *   send: () => send(),
 * })
 * ```
 */
export function useSpeechToText(options: UseSpeechToTextOptions) {
  const { $listen, $speak, chatbox, showSuggestion, send } = options

  // ====================
  // State
  // ====================

  /** Voice recognition instance */
  const voice = ref<VoiceRecognition | null>(null)

  /** Whether currently listening for voice input */
  const listening = ref(false)

  /** Whether push-to-talk mode is active */
  const showPushToTalk = ref(false)

  // ====================
  // Methods
  // ====================

  /**
   * Toggle voice input mode
   * Initializes speech recognition when enabled
   */
  function toggleVoice() {
    showPushToTalk.value = !showPushToTalk.value

    if (showPushToTalk.value) {
      showSuggestion.value = false

      if ($listen) {
        voice.value = $listen((text, _complete) => {
          if (text) {
            chatbox.value = text.trim()
          }
        })
      }
    } else {
      showSuggestion.value = true
    }
  }

  /**
   * Start listening for voice input
   */
  function startListen() {
    listening.value = true
    voice.value?.start()
  }

  /**
   * Stop listening and send the recognized text
   */
  function stopListen() {
    if (listening.value) {
      listening.value = false
      voice.value?.stop()
      send()

      setTimeout(() => {
        chatbox.value = ''
      }, 500)
    }
  }

  /**
   * Speak text using text-to-speech
   * Only speaks if click target is not a link
   *
   * @param text - Text to speak
   * @param event - Mouse event from click
   */
  function speak(text: string, event: MouseEvent) {
    if ((event.target as HTMLElement).tagName !== 'A') {
      $speak?.(text)
    }
  }

  return {
    // State
    voice,
    listening,
    showPushToTalk,

    // Methods
    toggleVoice,
    startListen,
    stopListen,
    speak,
  }
}
